/**
 * <bg-rings> — concentric expanding rings (ripples) from random origins.
 *
 * Sacred-geometry feel: thin gold-soft circles bloom outward, fade, die.
 * Pure Canvas2D. Stem assignments:
 *
 *   Lead vocals    → LIFETIME (sustained vocal = ring lives long)
 *   Backing vocals → HUE band (warm shift)
 *   Drums          → EXPANSION speed
 *   Bass           → STROKE width
 *   Guitar         → WOBBLE (per-ring radial perturbation)
 *   Percussion     → INNER pulse glow on the youngest ring
 *   Strings        → SPAWN rate (pads = constant new rings)
 *   Synth          → OUTER glow halo
 */

import { readAllStems, mix, clamp } from "./stem-levels.ts";
import { getCanvasDpr, getFrameBudgetMs, getParticleScale, shouldRender } from "./perf-budget.ts";

const MAX_RINGS = Math.max(3, Math.floor(24 * getParticleScale()));
const SPAWN_COOLDOWN_MS = 640;   // 320 → 640 (halve spawn rate)
const FRAME_BUDGET_MS = getFrameBudgetMs("atmosphere");

type Ring = {
  cx: number;
  cy: number;
  speed: number;     // px/sec growth
  thickness: number;
  hue: number;
  wobble: number;
  wobblePhase: number;
  bornAt: number;
  lifeMs: number;
};

const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

class BgRings extends HTMLElement {
  #canvas: HTMLCanvasElement | null = null;
  #ctx: CanvasRenderingContext2D | null = null;
  #rafId: number | null = null;
  #lastFrame: number = 0;
  #lastSpawn: number = 0;
  #rings: Ring[] = [];
  #w: number = 0;
  #h: number = 0;
  #resizeObserver: ResizeObserver | null = null;
  #visibilityHandler: (() => void) | null = null;

  connectedCallback(): void {
    if (!shouldRender("rings")) return;
    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;display:block;";
    this.style.position = this.style.position || "fixed";
    this.style.inset = "0";
    this.style.pointerEvents = "none";
    this.style.zIndex = "0";
    this.style.mixBlendMode = "screen";
    this.appendChild(canvas);
    this.#canvas = canvas;
    this.#ctx = canvas.getContext("2d");
    if (this.#ctx === null) return;
    this.#resize();
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);
    this.#visibilityHandler = (): void => {
      if (document.hidden) this.#stop();
      else this.#start();
    };
    document.addEventListener("visibilitychange", this.#visibilityHandler);
    this.#start();
  }

  disconnectedCallback(): void {
    this.#stop();
    this.#resizeObserver?.disconnect();
    if (this.#visibilityHandler !== null) {
      document.removeEventListener("visibilitychange", this.#visibilityHandler);
      this.#visibilityHandler = null;
    }
    this.#rings = [];
  }

  #resize(): void {
    if (this.#canvas === null || this.#ctx === null) return;
    const dpr = getCanvasDpr();
    this.#w = this.clientWidth || window.innerWidth;
    this.#h = this.clientHeight || window.innerHeight;
    this.#canvas.width = Math.max(1, Math.floor(this.#w * dpr));
    this.#canvas.height = Math.max(1, Math.floor(this.#h * dpr));
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  #spawn(now: number, drums: number, bass: number, backing: number, guitar: number, leadVox: number): void {
    if (this.#rings.length >= MAX_RINGS) this.#rings.shift();
    // Origin biased toward viewport edges (rings ripple in toward focus)
    const cx = rand(this.#w * 0.1, this.#w * 0.9);
    const cy = rand(this.#h * 0.1, this.#h * 0.9);
    this.#rings.push({
      cx, cy,
      speed: mix(28, 200, drums) + rand(-8, 8),
      thickness: mix(0.6, 3.0, bass),
      hue: 45 + (backing - 0.5) * 80,           // gold ± warm/cool
      wobble: mix(0, 8, guitar),
      wobblePhase: Math.random() * Math.PI * 2,
      bornAt: now,
      // LEAD VOCALS → lifetime (sustained melody = long-lived ring)
      lifeMs: mix(2600, 7500, leadVox),
    });
  }

  #start(): void {
    if (this.#rafId !== null) return;
    this.#lastFrame = performance.now();
    const tick = (now: number): void => {
      const dt = now - this.#lastFrame;
      if (dt >= FRAME_BUDGET_MS) {
        this.#lastFrame = now;
        this.#step(Math.min(50, dt), now);
        this.#render(now);
      }
      this.#rafId = requestAnimationFrame(tick);
    };
    this.#rafId = requestAnimationFrame(tick);
  }

  #stop(): void {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #step(_dt: number, now: number): void {
    const s = readAllStems();
    // STRINGS → spawn rate (sustained pads = constant ripple). Floor
    // lowered so rings spawn even at silence; peak doubled.
    const cooldown = mix(900, 140, s.strings);
    if (now - this.#lastSpawn >= cooldown) {
      this.#spawn(now, s.drums, s.bass, s["backing-vocals"], s.guitar, s["lead-vocals"]);
      this.#lastSpawn = now;
    }
    // Cull expired
    for (let i = this.#rings.length - 1; i >= 0; i -= 1) {
      const r = this.#rings[i];
      if (!r) continue;
      const age = now - r.bornAt;
      if (age > r.lifeMs) this.#rings.splice(i, 1);
    }
  }

  #render(now: number): void {
    if (this.#ctx === null) return;
    const ctx = this.#ctx;
    const s = readAllStems();
    ctx.clearRect(0, 0, this.#w, this.#h);
    ctx.globalCompositeOperation = "lighter";

    // Synth → outer halo glow size; percussion → youngest-ring inner pulse
    const synthGlow = mix(4, 26, s.synth);
    const percPulse = s.percussion;

    let youngestAge = Infinity;
    for (const r of this.#rings) {
      const age = now - r.bornAt;
      if (age < youngestAge) youngestAge = age;
    }

    for (const r of this.#rings) {
      const age = now - r.bornAt;
      const lifeT = clamp(age / r.lifeMs, 0, 1);
      const radius = r.speed * (age / 1000);
      // Envelope: fade in fast, fade out smooth
      let envelope = 1;
      if (lifeT < 0.08) envelope = lifeT / 0.08;
      else envelope = 1 - lifeT;
      envelope = clamp(envelope, 0, 1);
      const alpha = envelope * 1.25;  // CAPPED at 1.0 by stroke alpha clamping

      // Wobble via perturbing radius along angle
      ctx.strokeStyle = `hsla(${r.hue}, 70%, 70%, ${alpha})`;
      ctx.lineWidth = r.thickness;
      ctx.shadowColor = `hsla(${r.hue}, 80%, 70%, ${alpha})`;
      ctx.shadowBlur = synthGlow;

      ctx.beginPath();
      const segs = 64;
      for (let i = 0; i <= segs; i += 1) {
        const theta = (i / segs) * Math.PI * 2;
        const wob = Math.sin(theta * 5 + r.wobblePhase + now * 0.002) * r.wobble;
        const rr = radius + wob;
        const x = r.cx + Math.cos(theta) * rr;
        const y = r.cy + Math.sin(theta) * rr;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Inner pulse on the youngest ring — percussion drives intensity
      if (age === youngestAge && percPulse > 0.05) {
        const pulseR = radius * 0.4;
        const grad = ctx.createRadialGradient(r.cx, r.cy, 0, r.cx, r.cy, pulseR);
        grad.addColorStop(0, `hsla(${r.hue}, 80%, 80%, ${alpha * percPulse * 0.6})`);
        grad.addColorStop(1, `hsla(${r.hue}, 80%, 80%, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(r.cx, r.cy, pulseR, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalCompositeOperation = "source-over";
  }
}

export const registerBgRings = (): void => {
  if (customElements.get("bg-rings") !== undefined) return;
  customElements.define("bg-rings", BgRings);
};
